import { useState, useEffect } from "react"
import { func, string, bool, node, oneOfType } from "prop-types"
import FormButton from "./FormButton"
import classNames from "classnames"
import { useScreenWidth, BREAKPOINTS } from "source/shared/hooks/useScreenWidth"
import { Icon } from "source/shared/components"
import { Button } from "@planningcenter/doxy-web"

const Field = ({
  label,
  value,
  persistedValue,
  isEditable = false,
  isRequired,
  isOptional,
  onEdit = () => null,
  onSave = () => null,
  onCancel = () => null,
  children,
  error,
}) => {
  const screenWidth = useScreenWidth()
  const [isEditing, setIsEditing] = useState(false)

  useEffect(() => {
    if (!isRequired) return

    setIsEditing(isRequired && !persistedValue)
  }, [isRequired, persistedValue])

  const handleEdit = () => {
    onEdit()
    setIsEditing(true)
  }

  const handleCancel = () => {
    onCancel()
    setIsEditing(false)
  }

  const handleSave = () => onSave(() => setIsEditing(false))

  const isSmAndDown = screenWidth <= BREAKPOINTS.SM
  const longLabel = label.length > 10

  const fieldGridLine = isSmAndDown
    ? {
        display: "grid",
        gridTemplateColumns: "1fr",
        marginBottom: "16px",
      }
    : {
        display: "grid",
        gridTemplateColumns: "88px 1fr",
        columnGap: "8px",
        marginBottom: !isEditing && !longLabel ? 0 : "8px",
      }

  return (
    <div css={fieldGridLine}>
      <label
        className={classNames(
          "fs-4 pb-0 fw-600",
          isEditing && isRequired ? "required-field" : "",
          error ? "c-ruby" : "c-tint2",
        )}
        css={{
          lineHeight: longLabel || isSmAndDown ? "16px" : "32px",
          marginBottom: isSmAndDown ? "4px" : 0,
        }}
      >
        {label}
      </label>

      <div className={!isEditing && "mb-1"}>
        {!isEditing && (
          <div className="d-f ai-c">
            <span
              className="fs-4 c-tint2"
              css={{ lineHeight: isSmAndDown ? "16px" : "32px" }}
            >
              {value}
            </span>
            {isEditable && value && (
              <Button
                text={
                  <Icon
                    className="c-tint3"
                    css={{ fontSize: "12px" }}
                    symbol="general#pencil"
                  />
                }
                onClick={handleEdit}
                size="sm"
                variant="naked"
              />
            )}
          </div>
        )}

        {isOptional && !isEditing && !value && (
          <FormButton
            onClick={handleEdit}
            text={`Add ${label.toLowerCase()}`}
          />
        )}

        {isEditing && (
          <>
            {children}
            {error && <div className="c-ruby">{error}</div>}
            <div className="d-f g-1">
              {!isRequired && onCancel && (
                <FormButton onClick={handleCancel} text="Remove" />
              )}
              {isEditable && <FormButton onClick={handleSave} text="Save" />}
            </div>
          </>
        )}
      </div>
    </div>
  )
}

Field.propTypes = {
  label: string,
  value: oneOfType([string, node]),
  persistedValue: string,
  isEditable: bool,
  isRequired: bool,
  isOptional: bool,
  onEdit: func,
  onSave: func,
  onCancel: func,
  children: node,
  error: string,
  isSmallScreen: bool,
}

export default Field
